'use client';

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SavedSearch,
  SavedSearchCount,
  SavedSearchInput,
  SavedSearchesResponse,
} from '@/lib/backend-api';
import { normalizeFilterParams } from '@/lib/search-filters';
import { useSession } from '@/hooks/use-session';
import type { PlanTier } from '@/lib/plan-limits';

const SAVED_SEARCHES_KEY = ['saved-searches'] as const;

/** Matches the backend's per-call cap — each stale id costs a real COUNT. */
export const COUNT_BATCH_SIZE = 5;

/**
 * Mirrors the backend's staleness window so a page whose numbers are all
 * fresh sends **no request at all**.
 *
 * The server would filter these out anyway, but only after the round trip —
 * and since `mv_search` rebuilds nightly, the steady state for a returning
 * user is that nothing needs recounting. Worth deciding client-side.
 */
const COUNT_STALE_AFTER_MS = 12 * 60 * 60 * 1000;

export function needsRecount(search: SavedSearch, now = Date.now()): boolean {
  if (search.last_count == null || !search.last_counted_at) return true;
  return now - new Date(search.last_counted_at).getTime() > COUNT_STALE_AFTER_MS;
}

/**
 * Hitting the cap is a product state, not an error — same stance as
 * `use-lists`, so the dialog opens instead of an error boundary catching it.
 */
export class SavedSearchLimitError extends Error {
  constructor(
    readonly limit: number,
    readonly tier: PlanTier,
    readonly existing: { id: number; name: string }[],
  ) {
    super(`Saved search limit ${limit} reached`);
    this.name = 'SavedSearchLimitError';
  }
}

export interface SavedSearchLimits {
  tier: PlanTier;
  savedSearches: number;
}

type SavedSearchesData = {
  items: SavedSearch[];
  limits: SavedSearchLimits | null;
};

async function fetchSavedSearches(): Promise<SavedSearchesData> {
  const res = await fetch('/api/saved-searches');
  if (!res.ok) throw new Error(`/api/saved-searches returned ${res.status}`);
  const json = await res.json();
  return {
    items: (((json.data ?? {}) as SavedSearchesResponse).items ?? []).map((item) => ({ ...item, filters: { ...normalizeFilterParams(item.filters) } })),
    limits: (json.limits ?? null) as SavedSearchLimits | null,
  };
}

async function throwIfCapped(res: Response): Promise<void> {
  if (res.status !== 403) return;
  const body = (await res.json().catch(() => null)) as {
    code?: string;
    limit?: number;
    tier?: string;
    existing?: { id: number; name: string }[];
  } | null;
  if (body?.code === 'saved_search_limit') {
    throw new SavedSearchLimitError(
      Number(body.limit) || 1,
      (body.tier as SavedSearchLimits['tier']) ?? 'standard',
      body.existing ?? [],
    );
  }
}

export function useSavedSearches() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  const query = useQuery<SavedSearchesData>({
    queryKey: SAVED_SEARCHES_KEY,
    queryFn: fetchSavedSearches,
    enabled: !!session,
    staleTime: 30 * 1000,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: SAVED_SEARCHES_KEY });
  }, [queryClient]);

  const create = useMutation({
    mutationFn: async (input: SavedSearchInput): Promise<SavedSearch> => {
      const res = await fetch('/api/saved-searches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      await throwIfCapped(res);
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      const saved = (await res.json()).data as SavedSearch;
      return { ...saved, filters: { ...normalizeFilterParams(saved.filters) } };
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<SavedSearchInput>;
    }): Promise<SavedSearch> => {
      const res = await fetch(`/api/saved-searches/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      const saved = (await res.json()).data as SavedSearch;
      return { ...saved, filters: { ...normalizeFilterParams(saved.filters) } };
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/saved-searches/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return {
    items: query.data?.items ?? [],
    limits: query.data?.limits ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    create,
    update,
    remove,
    invalidate,
  };
}

/**
 * Refresh match counts in batches, patching the cached rows as they land.
 *
 * Deliberately not part of the list query: the cards must paint from stored
 * numbers immediately, because a cold count is a COUNT over `mv_search` and
 * measures in seconds for broad filters. Batches are sequential so a page of
 * fifty cards cannot open fifty concurrent counts.
 */
export function useRefreshCounts() {
  const queryClient = useQueryClient();

  return useCallback(
    async (ids: number[]) => {
      for (let i = 0; i < ids.length; i += COUNT_BATCH_SIZE) {
        const batch = ids.slice(i, i + COUNT_BATCH_SIZE);
        try {
          const res = await fetch('/api/saved-searches/refresh-counts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: batch }),
          });
          if (!res.ok) continue;
          const counts = ((await res.json()).data?.counts ?? []) as SavedSearchCount[];
          queryClient.setQueryData<SavedSearchesData>(SAVED_SEARCHES_KEY, (prev) => {
            if (!prev) return prev;
            const byId = new Map(counts.map((c) => [c.id, c]));
            return {
              ...prev,
              items: prev.items.map((s) => {
                const c = byId.get(s.id);
                return c
                  ? {
                      ...s,
                      last_count: c.count,
                      last_counted_at: c.counted_at,
                      new_matches: c.new_matches,
                    }
                  : s;
              }),
            };
          });
        } catch {
          // A failed batch leaves the stored numbers in place; the card
          // shows its "updated …" timestamp, which is the honest state.
        }
      }
    },
    [queryClient],
  );
}

/** Advance the badge baseline — call when the user actually opens results. */
export function useMarkOpened() {
  const queryClient = useQueryClient();
  return useCallback(
    (id: number) => {
      void fetch(`/api/saved-searches/${id}/opened`, { method: 'POST' })
        .then(() => queryClient.invalidateQueries({ queryKey: SAVED_SEARCHES_KEY }))
        .catch(() => {
          // Best-effort: a missed baseline bump only means the badge shows
          // again next time, which is far better than blocking navigation.
        });
    },
    [queryClient],
  );
}
